import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserCheck, Loader2, Search } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const ROLE_LABEL = {
  compliance_director: "Compliance Director",
  senior_verification_manager: "Senior Verification Manager",
  super_admin: "Super Admin",
};

// Searchable picker of eligible legal/compliance reviewers. Only backend-approved authority
// roles appear; assignment RBAC is enforced server-side.
export default function ReviewerPickerDialog({ contract, open, onOpenChange, onDone }) {
  const [reviewers, setReviewers] = useState([]);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const current = contract.assigned_reviewer;

  useEffect(() => {
    if (!open) return;
    setPicked(null); setReason(""); setQ("");
    api.get("/contracts/eligible-reviewers")
      .then(({ data }) => setReviewers(data.reviewers || []))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || "Could not load reviewers"));
  }, [open]);

  const filtered = reviewers.filter((r) => {
    const s = `${r.name || ""} ${r.email || ""} ${ROLE_LABEL[r.role] || r.role}`.toLowerCase();
    return s.includes(q.toLowerCase());
  });
  const reassigning = current && picked && picked.user_id !== current.user_id;

  const submit = async () => {
    if (!picked) return toast.error("Select a reviewer");
    if (reassigning && !reason.trim()) return toast.error("A reason is required to reassign");
    setBusy(true);
    try {
      await api.post(`/contracts/${contract.contract_id}/assign-reviewer`,
        { reviewer_user_id: picked.user_id, reason });
      toast.success(`Reviewer assigned: ${picked.name || picked.email}`);
      onOpenChange(false); onDone?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Assignment failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent data-testid="reviewer-picker-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserCheck size={18} className="text-blue-600" /> Assign legal reviewer</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {current && <p className="text-xs text-slate-500">Currently assigned: <strong>{current.name || current.email}</strong> ({ROLE_LABEL[current.reviewer_role] || current.reviewer_role})</p>}
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
            <Input data-testid="reviewer-search" className="pl-8" placeholder="Search by name, email or role" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="max-h-52 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-1" data-testid="reviewer-list">
            {filtered.length === 0 && <p className="p-2 text-xs text-slate-400">No eligible reviewers found.</p>}
            {filtered.map((r) => (
              <button key={r.user_id} data-testid={`reviewer-opt-${r.user_id}`} onClick={() => setPicked(r)}
                className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-left ${picked?.user_id === r.user_id ? "bg-blue-50 ring-1 ring-blue-300" : "hover:bg-slate-50"}`}>
                <span>
                  <span className="font-medium text-slate-800">{r.name || r.email}</span>
                  <span className="ml-1 text-[11px] text-slate-400">{r.email}</span>
                </span>
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">{ROLE_LABEL[r.role] || r.role}</span>
              </button>
            ))}
          </div>
          {reassigning && (
            <div>
              <Label htmlFor="reassign-reason" className="text-xs">Reason for reassignment</Label>
              <Textarea id="reassign-reason" data-testid="reassign-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
          )}
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} disabled={busy} className="h-9 px-4 rounded-md border border-slate-300 text-sm">Cancel</button>
          <button data-testid="reviewer-assign-submit" onClick={submit} disabled={busy || !picked || (reassigning && !reason.trim())}
            className="h-9 px-4 rounded-md bg-blue-600 text-white text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <UserCheck size={14} />} Assign
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
